import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, View, Platform, Pressable, Text } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { useRouter } from 'expo-router'
import { useMutation } from 'convex/react'
import { Picker } from '@react-native-picker/picker'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { api } from '@ppwr/convex/_generated/api'
import {
  BLIND_LEVELS,
  TOURNAMENT_DEFAULTS,
  TOURNAMENT_STARTING_CHIPS_TO_BIG_BLIND_RATIO,
  getAllowedStartingBlindLevels,
  getBlindLevelIndexForBigBlind
} from '@ppwr/convex/shared/tournamentConstants'
import { ReduceMotion } from 'react-native-reanimated'
import { DS } from '@/design-system'
import { ScreenHeader } from '@/components/ScreenHeader'
import { SettingsCard } from '@/components/layouts/SettingsCard'
import { PillPicker } from '@/components/PillPicker'
import { FormTextInput } from '@/screens/new-game/components/FormTextInput'
import { Button } from '@/components/ANewDesign/Button/Button'
import { fontFamily } from '../../theme'
import { numberWithCommas } from '@/utils/numberWithCommas'
import { useStore } from '@/models'
import { toastNotificationStore } from '@/components/ANewDesign/ToastNotification/ToastNotification'
import { events } from '@/models/analytics/analytics-events'
import { MTTDateTimePickers } from './MTTDateTimePickers'
import { redirectToLobby } from './mttNavigation'

const MIN_STARTING_CHIPS = BLIND_LEVELS[0] * TOURNAMENT_STARTING_CHIPS_TO_BIG_BLIND_RATIO
const MAX_STARTING_CHIPS = 10_000_000
const SCHEDULED_START_LEEWAY_MS = 30_000

const GRACE_PERIOD_OPTIONS = [
  { label: '5', value: 300_000 },
  { label: '15', value: 900_000 },
  { label: '30', value: 1_800_000 }
]

const SEATS_PER_TABLE_OPTIONS = [
  { label: '4', value: 4 },
  { label: '6', value: 6 },
  { label: '8', value: 8 },
  { label: '9', value: 9 },
  { label: '10', value: 10 }
]

const MIN_TABLES_OPTIONS = [
  { label: '2', value: 2 },
  { label: '4', value: 4 },
  { label: '8', value: 8 },
  { label: '12', value: 12 }
]

const REENTRY_OPTIONS = [
  { label: 'None', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 }
]

const BLIND_INTERVAL_OPTIONS = [
  { label: '3', value: 3 },
  { label: '5', value: 5 },
  { label: '10', value: 10 }
]

const GRACE_PERIOD_PILL_OPTIONS = GRACE_PERIOD_OPTIONS.map((opt, i) => ({ label: opt.label, value: i }))

function getStartingChipsValidationError(args: {
  parsedStartingChips: number
  minStartingChipsForSelectedBlind: number
  startingBlindLabel: string
}) {
  const { parsedStartingChips, minStartingChipsForSelectedBlind, startingBlindLabel } = args

  if (!(Number.isFinite(parsedStartingChips) && Number.isInteger(parsedStartingChips))) {
    return `Starting chips must be a whole number between ${MIN_STARTING_CHIPS} and ${MAX_STARTING_CHIPS}.`
  }

  if (parsedStartingChips < MIN_STARTING_CHIPS || parsedStartingChips > MAX_STARTING_CHIPS) {
    return `Starting chips must be a whole number between ${MIN_STARTING_CHIPS} and ${MAX_STARTING_CHIPS}.`
  }

  if (parsedStartingChips < minStartingChipsForSelectedBlind) {
    return `Starting chips must be at least ${minStartingChipsForSelectedBlind} for starting blind ${startingBlindLabel}.`
  }

  return null
}

export function CreateMTTScreen() {
  const router = useRouter()
  const store = useStore()
  const { user, isAdmin, isSuperAdmin } = store.session
  const { setEvent } = store.analytics
  const userId = user?.auth0Id || ''
  const canUseTestingTableControls = isAdmin || isSuperAdmin

  const createTournament = useMutation(api.tournaments.mutations.createTournament)
  const startingBlindSheetRef = useRef<BottomSheetModal>(null)

  useEffect(() => {
    setEvent(events.mtt.create_screen_viewed)
  }, [setEvent])

  const [name, setName] = useState('')
  const [startingChips, setStartingChips] = useState(String(TOURNAMENT_DEFAULTS.STARTING_CHIPS))
  const [startingBigBlind, setStartingBigBlind] = useState<number>(BLIND_LEVELS[0])
  const [blindInterval, setBlindInterval] = useState(3)
  const [maxPlayersPerTable, setMaxPlayersPerTable] = useState(9)
  const [gracePeriodIdx, setGracePeriodIdx] = useState(1) // default 15 min
  const [maxReentries, setMaxReentries] = useState(0)
  const [minTables, setMinTables] = useState<number | undefined>(undefined)
  const [pendingStartingBigBlind, setPendingStartingBigBlind] = useState<number>(BLIND_LEVELS[0])
  const [scheduledStart, setScheduledStart] = useState(() => {
    // Default to 10 minutes from now, rounded up to next 5-minute mark
    const d = new Date(Date.now() + 10 * 60 * 1000)
    const remainder = d.getMinutes() % 5
    if (remainder !== 0) d.setMinutes(d.getMinutes() + (5 - remainder))
    d.setSeconds(0, 0)
    return d
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const trimmedName = name.trim()
  const parsedStartingChips = Number(startingChips)
  const allowedStartingBlindLevels = useMemo(
    () => getAllowedStartingBlindLevels(Number.isFinite(parsedStartingChips) ? parsedStartingChips : 0),
    [parsedStartingChips]
  )
  const largestAllowedStartingBigBlind = allowedStartingBlindLevels[allowedStartingBlindLevels.length - 1]?.bigBlind
  const startingSmallBlind = Math.floor(startingBigBlind / 2)
  const startingBlindsLabel = `${startingBigBlind}/${startingSmallBlind}`
  const minStartingChipsForSelectedBlind = startingBigBlind * TOURNAMENT_STARTING_CHIPS_TO_BIG_BLIND_RATIO
  const startingBlindLevelIndex = getBlindLevelIndexForBigBlind(startingBigBlind)
  const gracePeriodMs = GRACE_PERIOD_OPTIONS[gracePeriodIdx].value
  const startingChipsValidationError = getStartingChipsValidationError({
    parsedStartingChips,
    minStartingChipsForSelectedBlind,
    startingBlindLabel: startingBlindsLabel
  })
  const tournamentConfig = {
    startingChips: parsedStartingChips,
    startingBigBlind,
    blindIncreaseIntervalMs: blindInterval * 60 * 1000,
    maxPlayersPerTable,
    gracePeriodMs,
    maxReentries,
    ...(canUseTestingTableControls && minTables !== undefined ? { minTables } : {})
  }
  const createSubmittedEventProperties = {
    startingChips: parsedStartingChips,
    startingBlindLevelIndex,
    startingBigBlind,
    startingSmallBlind,
    blindIntervalMinutes: blindInterval,
    maxPlayersPerTable,
    gracePeriodMs,
    maxReentries,
    hasMinTables: minTables !== undefined,
    minTables: minTables ?? null
  }
  const startingBlindSheetSnapPoints = useMemo(() => [320], [])

  useEffect(() => {
    if (largestAllowedStartingBigBlind === undefined) return
    if (startingBigBlind > largestAllowedStartingBigBlind) {
      setStartingBigBlind(largestAllowedStartingBigBlind)
    }
  }, [largestAllowedStartingBigBlind, startingBigBlind])

  const showError = (text: string) => {
    // Route through the queue-aware API so back-to-back validation errors
    // (e.g. typing the form quickly) don't clobber each other — the previous
    // direct `setState` write replaced the visible message and lost the
    // earlier one.
    toastNotificationStore.getState().show({ text })
  }

  const handleSelectStartingBlind = (nextStartingBigBlind: number) => {
    const minStartingChipsForBlind = nextStartingBigBlind * TOURNAMENT_STARTING_CHIPS_TO_BIG_BLIND_RATIO

    if (!Number.isFinite(parsedStartingChips) || parsedStartingChips < minStartingChipsForBlind) {
      setStartingChips(String(minStartingChipsForBlind))
    }

    setStartingBigBlind(nextStartingBigBlind)
  }

  const renderStartingBlindBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} pressBehavior="close" disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.3} />
    ),
    []
  )

  const openStartingBlindSelector = () => {
    setPendingStartingBigBlind(startingBigBlind)
    startingBlindSheetRef.current?.present()
  }

  const closeStartingBlindSelector = () => {
    startingBlindSheetRef.current?.dismiss()
    setPendingStartingBigBlind(startingBigBlind)
  }

  const confirmStartingBlindSelector = () => {
    handleSelectStartingBlind(pendingStartingBigBlind)
    startingBlindSheetRef.current?.dismiss()
  }

  const handleCreate = async () => {
    if (!(trimmedName && userId)) return

    const scheduledStartTime = scheduledStart.getTime()
    if (scheduledStartTime <= Date.now() + SCHEDULED_START_LEEWAY_MS) {
      showError('Pick a start time at least a minute in the future.')
      return
    }

    if (startingChipsValidationError) {
      showError(startingChipsValidationError)
      return
    }

    setIsSubmitting(true)

    try {
      const result = await createTournament({
        name: trimmedName,
        hostUserId: userId,
        scheduledStartTime,
        config: tournamentConfig
      })

      setEvent(events.mtt.create_submitted, {
        tournamentId: result.tournamentId,
        ...createSubmittedEventProperties
      })

      redirectToLobby(router, result.tournamentId)
    } catch (err) {
      console.error('Failed to create tournament:', err)
      showError(err instanceof Error ? err.message : 'Could not create tournament. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <StatusBar style="dark" />
      <ScreenHeader
        title="Create Tournament"
        onBack={() => (router.canGoBack() ? router.back() : router.replace('/(root)/(tabs)/play'))}
      />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={styles.webWrapper}>
          <SettingsCard gap={24} style={styles.card}>
            <FormTextInput label="Name" value={name} onChangeText={setName} placeholder="e.g. Friday Night Poker" />

            <MTTDateTimePickers value={scheduledStart} onChange={setScheduledStart} />

            <DS.Frame row gap="20">
              <View style={styles.configField}>
                <FormTextInput
                  label="Starting Chips"
                  value={startingChips === '' ? '' : numberWithCommas(Number(startingChips))}
                  onChangeText={(val) => setStartingChips(val.replace(/\D/g, ''))}
                  keyboardType="numeric"
                  placeholder="10,000"
                />
              </View>

              <DS.Frame gap="6" style={styles.configField}>
                <DS.Text variant="Body Medium/Regular" style={styles.fieldLabel}>
                  Starting Blinds
                </DS.Text>
                <View style={styles.pickerShell}>
                  {Platform.OS === 'ios' ? (
                    <Pressable onPress={openStartingBlindSelector} style={styles.input}>
                      <Text style={styles.inputValue}>{startingBlindsLabel}</Text>
                    </Pressable>
                  ) : Platform.OS === 'web' ? (
                    <select
                      value={String(startingBigBlind)}
                      onChange={(event) => handleSelectStartingBlind(Number(event.target.value))}
                      style={styles.webSelect}
                    >
                      {allowedStartingBlindLevels.map((level) => (
                        <option key={level.index} value={String(level.bigBlind)}>
                          {`${level.bigBlind}/${level.smallBlind}`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Picker
                      selectedValue={startingBigBlind}
                      onValueChange={(value) => handleSelectStartingBlind(Number(value))}
                      style={styles.picker}
                      itemStyle={styles.pickerItem}
                      dropdownIconColor={DS.palette['Global/Black']}
                      mode="dropdown"
                    >
                      {allowedStartingBlindLevels.map((level) => (
                        <Picker.Item
                          key={level.index}
                          label={`${level.bigBlind}/${level.smallBlind}`}
                          value={level.bigBlind}
                          color={DS.palette['Global/Black']}
                        />
                      ))}
                    </Picker>
                  )}
                </View>
              </DS.Frame>
            </DS.Frame>

            <DS.Frame gap="8">
              <DS.Text variant="Body Medium/Regular" style={styles.fieldLabel}>
                Blind Increase Interval (min)
              </DS.Text>
              <PillPicker options={BLIND_INTERVAL_OPTIONS} value={blindInterval} onChange={setBlindInterval} />
            </DS.Frame>

            <DS.Frame gap="8">
              <DS.Text variant="Body Medium/Regular" style={styles.fieldLabel}>
                Seats per Table
              </DS.Text>
              <PillPicker
                options={SEATS_PER_TABLE_OPTIONS}
                value={maxPlayersPerTable}
                onChange={setMaxPlayersPerTable}
              />
            </DS.Frame>

            <DS.Frame gap="8">
              <DS.Text variant="Body Medium/Regular" style={styles.fieldLabel}>
                Sitting Out Grace Period (min)
              </DS.Text>
              <PillPicker options={GRACE_PERIOD_PILL_OPTIONS} value={gracePeriodIdx} onChange={setGracePeriodIdx} />
            </DS.Frame>

            <DS.Frame gap="8">
              <DS.Text variant="Body Medium/Regular" style={styles.fieldLabel}>
                Max Re-entries
              </DS.Text>
              <PillPicker options={REENTRY_OPTIONS} value={maxReentries} onChange={setMaxReentries} />
            </DS.Frame>

            {canUseTestingTableControls && (
              <DS.Frame gap="8">
                <DS.Frame row gap="8" style={{ alignItems: 'baseline' }}>
                  <DS.Text variant="Body Medium/Regular" style={styles.fieldLabel}>
                    Minimum Tables (testing)
                  </DS.Text>
                  <DS.Text variant="Mini/Regular" style={{ color: '#999' }}>
                    (optional)
                  </DS.Text>
                </DS.Frame>
                <DS.Text variant="Mini/Regular" style={{ color: '#666' }}>
                  Testing option: force extra tables and fill empty seats with bots
                </DS.Text>
                <PillPicker
                  options={MIN_TABLES_OPTIONS}
                  value={minTables}
                  onChange={(v) => setMinTables(minTables === v ? undefined : v)}
                />
              </DS.Frame>
            )}
          </SettingsCard>
        </View>
      </ScrollView>

      <View style={styles.footer}>
        {isSubmitting ? (
          <ActivityIndicator color={DS.palette['Brand/Maroon']} style={styles.loader} />
        ) : (
          <Button
            text="Create Tournament"
            size="large"
            type="primary"
            onPress={trimmedName && userId ? handleCreate : undefined}
            containerStyle={[styles.startButton]}
            textStyle={[styles.startButtonText]}
          />
        )}
      </View>
      {Platform.OS === 'ios' && (
        <BottomSheetModal
          ref={startingBlindSheetRef}
          index={0}
          snapPoints={startingBlindSheetSnapPoints}
          backdropComponent={renderStartingBlindBackdrop}
          detached={false}
          overrideReduceMotion={ReduceMotion.System}
          enableDynamicSizing={false}
          enablePanDownToClose={true}
          enableOverDrag={false}
          onDismiss={() => setPendingStartingBigBlind(startingBigBlind)}
          handleIndicatorStyle={styles.sheetHandleIndicator}
          backgroundStyle={styles.sheetBackground}
        >
          <BottomSheetView style={styles.sheetContent}>
            <View style={styles.sheetActions}>
              <DS.Button type="On Dark" variant="Medium/Secondary" onPress={closeStartingBlindSelector}>
                Cancel
              </DS.Button>
              <DS.Button type="On Light" variant="Medium/Primary" onPress={confirmStartingBlindSelector}>
                Done
              </DS.Button>
            </View>
            <Picker
              selectedValue={pendingStartingBigBlind}
              onValueChange={(value) => setPendingStartingBigBlind(Number(value))}
              itemStyle={styles.iosPickerItem}
            >
              {allowedStartingBlindLevels.map((level) => (
                <Picker.Item
                  key={level.index}
                  label={`${level.bigBlind}/${level.smallBlind}`}
                  value={level.bigBlind}
                  color={DS.palette['Global/Black']}
                />
              ))}
            </Picker>
          </BottomSheetView>
        </BottomSheetModal>
      )}
    </SafeAreaView>
  )
}

// Shared 48px bordered field box, same as the create-game inputs.
const inputBox = {
  height: 48,
  borderWidth: 1,
  borderColor: DS.palette['Brand/Light Grey'],
  borderRadius: 8,
  paddingHorizontal: 12,
  backgroundColor: DS.palette['Global/White'],
  justifyContent: 'center' as const
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  scroll: {
    flex: 1
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 24
  },
  card: {
    borderRadius: 16,
    borderWidth: 0,
    padding: 24
  },
  fieldLabel: {
    color: DS.palette['Global/Black']
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 8
  },
  startButton: {
    backgroundColor: DS.palette['Brand/Maroon'],
    borderRadius: 48,
    paddingVertical: 14.5
  },
  startButtonText: {
    fontFamily: fontFamily.GTAmericaStandardMedium,
    fontSize: 16,
    fontWeight: '500'
  },
  loader: {
    paddingVertical: 14
  },
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center' as const
  },
  configField: {
    flex: 1
  },
  input: inputBox,
  // Matches the value text in the create-game SelectPicker / ScheduleDateTimeFields.
  inputValue: {
    fontSize: 16,
    color: DS.palette['Global/Black']
  },
  // iOS renders its own bordered Pressable (sheet trigger) inside this shell.
  // Web and Android wrap the native select/Picker in the same box; the native
  // control supplies its own horizontal padding.
  pickerShell: Platform.OS === 'ios' ? {} : { ...inputBox, paddingHorizontal: 0, overflow: 'hidden' as const },
  picker: {
    width: '100%',
    height: 48
  },
  pickerItem: {
    color: DS.palette['Global/Black']
  },
  webSelect: {
    width: '100%',
    height: '100%',
    borderWidth: 0,
    outlineStyle: 'none' as 'solid',
    backgroundColor: '#fff',
    paddingLeft: 12,
    paddingRight: 12,
    fontSize: 16,
    color: DS.palette['Global/Black']
  },
  iosPickerItem: {
    color: DS.palette['Global/Black']
  },
  sheetHandleIndicator: {
    backgroundColor: '#ddd'
  },
  sheetBackground: {
    backgroundColor: '#fff'
  },
  sheetContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 24
  },
  sheetActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8
  }
})
